import { useRef, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button, Spinner, Badge, Input } from 'reactstrap';
import { BiArrowBack, BiRefresh, BiCopy, BiShow, BiDownload, BiTrash, BiMailSend, BiTime } from 'react-icons/bi';
import useQuote from '../hooks/useQuote';
import useExportQuotePdf from '../hooks/useExportQuotePdf';
import useDuplicateQuote from '../hooks/useDuplicateQuote';
import useTierFeatures from '../hooks/useTierFeatures';
import useBillingUsage from '../hooks/useBillingUsage';
import useIsOrgAdmin from '../hooks/useIsOrgAdmin';
import useUpdateQuote from '../hooks/useUpdateQuote';
import QuoteDetailsCard from '../components/quote/quoteDetailsCard';
import QuoteLineItemsTable from '../components/quote/quoteLineItemsTable';
import ConvertQuoteModal from '../components/quote/convertQuoteModal';
import DuplicateQuoteModal from '../components/quote/duplicateQuoteModal';
import PreviewQuotePdfModal from '../components/quote/previewQuotePdfModal';
import DeleteQuoteModal from '../components/quote/deleteQuoteModal';
import SendQuoteModal from '../components/quote/sendQuoteModal';
import ChangePlanModal from '../components/billing/ChangePlanModal';
import { isQuoteExpired, STATUS_COLORS, QUOTE_STATUS_EDIT_OPTIONS } from '../utils/quoteStatuses';

export default function ViewQuotePage() {
    const { id: idParam } = useParams();
    const id = Number(idParam);
    // `dirtyRef` is shared with the quote query hook (so SignalR invalidations
    // pause while autosave is in flight) and the Details card (which owns it).
    const dirtyRef = useRef(false);
    const {
        data: quote, isLoading, error, flushPendingRefresh,
    } = useQuote(id, dirtyRef);

    const navigate = useNavigate();
    const duplicateQuote = useDuplicateQuote();
    const [revising, setRevising] = useState(false);

    const handleRevise = useCallback(() => {
        if (!quote?.id || revising) return;
        setRevising(true);
        duplicateQuote.mutate({ id: quote.id, customerId: quote.customerId }, {
            onSuccess: ({ quoteId }) => navigate(`/quotes/${quoteId}`),
            onSettled: () => setRevising(false),
        });
    }, [quote?.id, revising, duplicateQuote, navigate]);

    const [convertOpen, setConvertOpen] = useState(false);
    const [duplicateOpen, setDuplicateOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [sendOpen, setSendOpen] = useState(false);
    const [planModalOpen, setPlanModalOpen] = useState(false);

    const { download, isDownloading } = useExportQuotePdf();
    const { quotePdfEnabled } = useTierFeatures();
    const { data: usage } = useBillingUsage();
    const isAdmin = useIsOrgAdmin();
    const updateQuote = useUpdateQuote();

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <Spinner />
            </div>
        );
    }

    // Same masking as viewCustomerPage — 404 covers both "doesn't exist" and
    // "belongs to a different tenant".
    if (error?.status === 404 || (!quote && !isLoading)) {
        return (
            <div>
                <Link to="/quotes" className="text-decoration-none d-inline-flex align-items-center gap-1 mb-3">
                    <BiArrowBack /> Quotes
                </Link>
                <div className="surface text-center py-5">
                    <h4 className="mb-2">Quote not found</h4>
                    <p className="text-muted mb-3">
                        This quote doesn't exist or you don't have access to it.
                    </p>
                    <Link to="/quotes">
                        <Button color="primary">Back to Quotes</Button>
                    </Link>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <Link to="/quotes" className="text-decoration-none d-inline-flex align-items-center gap-1 mb-3">
                    <BiArrowBack /> Quotes
                </Link>
                <div className="surface text-center py-5 text-danger">
                    Failed to load quote. Please try again.
                </div>
            </div>
        );
    }

    const isConverted = quote.convertedToJobId != null;
    const locked = isConverted || quote.status === 'Sent' || quote.status === 'Accepted' || quote.status === 'Rejected';
    const expired = isQuoteExpired(quote);
    const hasIncompleteItems = (quote.lineItems ?? []).length === 0 || (quote.lineItems ?? []).some(
        item => !item.description?.trim() || !(Number(item.quantity) > 0)
    );

    return (
        <div>
            <Link to="/quotes" className="text-decoration-none d-inline-flex align-items-center gap-1 mb-2">
                <BiArrowBack /> Quotes
            </Link>

            <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
                <div>
                    <h1 className="fw-bold mb-1">{quote.title || 'Untitled quote'}</h1>
                    <p className="text-muted mb-0 d-flex align-items-center gap-2 flex-wrap">
                        {quote.quoteNumber && <span>#{quote.quoteNumber}</span>}
                        {quote.status && (() => {
                            const sc = STATUS_COLORS[quote.status] || STATUS_COLORS.Draft;
                            return (
                                <>
                                    <span>·</span>
                                    <span style={{ backgroundColor: sc.bg, color: sc.text, padding: '2px 10px', borderRadius: 12, fontSize: 13, fontWeight: 600 }}>
                                        {quote.status}
                                    </span>
                                </>
                            );
                        })()}
                        {expired && (
                            <span style={{ backgroundColor: STATUS_COLORS.Expired.bg, color: STATUS_COLORS.Expired.text, padding: '2px 10px', borderRadius: 12, fontSize: 13, fontWeight: 600 }}>
                                Expired
                            </span>
                        )}
                        {quote.customerName && (
                            <>
                                <span>·</span>
                                <Link to={`/customers/${quote.customerId}`}>
                                    {quote.customerName}
                                </Link>
                            </>
                        )}
                    </p>
                </div>
            </div>

            {/* Waiting banner — hidden for Solo tier (can't send emails) */}
            {quote.status === 'Sent' && quotePdfEnabled && (
                <div
                    className="d-flex align-items-center gap-2 px-3 py-3 rounded mb-3"
                    style={{ background: '#dbeafe', color: '#2563eb', fontSize: '0.9rem' }}
                >
                    <BiTime size={18} /> Waiting for customer response to quote...
                </div>
            )}

            <div className="mb-4">
                <QuoteDetailsCard
                    quote={quote}
                    dirtyRef={dirtyRef}
                    flushPendingRefresh={flushPendingRefresh}
                    quotePdfEnabled={quotePdfEnabled}
                />
            </div>

            <h2 className="fw-semibold fs-4 mb-3">Line Items</h2>
            <div className="mb-4">
                <QuoteLineItemsTable quote={quote} />
            </div>

            {/* Action row. Convert + Delete are locked on a converted quote;
                Duplicate and Preview/Export stay enabled so the user can
                still branch from a successful quote and export the PDF. */}
            <div className="surface d-flex flex-wrap gap-2 mb-4">
                {/* Draft: Send Quote */}
                {quote.status === 'Draft' && (
                    quotePdfEnabled ? (
                        <Button color="primary"
                                onClick={() => setSendOpen(true)}
                                disabled={hasIncompleteItems}
                                title={hasIncompleteItems ? 'Complete all line items before sending' : undefined}
                                className="d-inline-flex align-items-center gap-1">
                            <BiMailSend /> Send Quote
                        </Button>
                    ) : (
                        // Solo tier — sending by email is gated. Admins get the
                        // upgrade modal directly; members see the disabled +
                        // Pro-badge pattern since they can't change plan
                        // (server enforces AdminOnly on /Billing/ChangeTier).
                        <span style={{ position: 'relative', display: 'inline-block' }}>
                            <Button color="primary"
                                    onClick={isAdmin ? () => setPlanModalOpen(true) : undefined}
                                    disabled={!isAdmin}
                                    title={isAdmin ? 'Upgrade to Pro to send quotes by email' : 'Ask an admin to upgrade to Pro to send quotes by email'}
                                    className="d-inline-flex align-items-center gap-1">
                                <BiMailSend /> Send Quote
                            </Button>
                            <span style={{
                                position: 'absolute', top: -6, right: -6,
                                backgroundColor: '#26ada9', color: '#fff',
                                fontSize: 10, fontWeight: 700,
                                padding: '1px 6px', borderRadius: 10,
                                lineHeight: '16px'
                            }}>Pro</span>
                        </span>
                    )
                )}

                {/* Rejected: Revise (duplicate as new draft, navigate to it) */}
                {quote.status === 'Rejected' && (
                    <Button color="primary"
                            onClick={handleRevise}
                            disabled={revising}
                            className="d-inline-flex align-items-center gap-1">
                        {revising ? <Spinner size="sm" /> : <BiRefresh />} Revise
                    </Button>
                )}

                {/* Accepted: Convert to Job */}
                {quote.status === 'Accepted' && !isConverted && (
                    <Button color="primary"
                            onClick={() => setConvertOpen(true)}
                            className="d-inline-flex align-items-center gap-1">
                        <BiRefresh /> Convert to Job
                    </Button>
                )}

                {/* Always available */}
                <Button color="secondary" outline
                        onClick={() => setDuplicateOpen(true)}
                        className="d-inline-flex align-items-center gap-1">
                    <BiCopy /> Duplicate
                </Button>

                {/* Preview PDF — gated for Solo tier. Same admin /
                    non-admin split as Send Quote above. */}
                {quotePdfEnabled ? (
                    <Button color="secondary" outline
                            onClick={() => setPreviewOpen(true)}
                            className="d-inline-flex align-items-center gap-1">
                        <BiShow /> Preview PDF
                    </Button>
                ) : (
                    <span style={{ position: 'relative', display: 'inline-block' }}>
                        <Button color="secondary" outline
                                onClick={isAdmin ? () => setPlanModalOpen(true) : undefined}
                                disabled={!isAdmin}
                                title={isAdmin ? 'Upgrade to Pro to preview quote PDFs' : 'Ask an admin to upgrade to Pro to preview quote PDFs'}
                                className="d-inline-flex align-items-center gap-1">
                            <BiShow /> Preview PDF
                        </Button>
                        <span style={{
                            position: 'absolute', top: -6, right: -6,
                            backgroundColor: '#26ada9', color: '#fff',
                            fontSize: 10, fontWeight: 700,
                            padding: '1px 6px', borderRadius: 10,
                            lineHeight: '16px'
                        }}>Pro</span>
                    </span>
                )}

                {/* Delete only when Draft */}
                {quote.status === 'Draft' && (
                    <div className="ms-auto">
                        <Button color="danger" outline
                                onClick={() => setDeleteOpen(true)}
                                className="d-inline-flex align-items-center gap-1">
                            <BiTrash /> Delete
                        </Button>
                    </div>
                )}
            </div>

            <ConvertQuoteModal   isOpen={convertOpen}   onClose={() => setConvertOpen(false)}   quote={quote} />
            <DuplicateQuoteModal isOpen={duplicateOpen} onClose={() => setDuplicateOpen(false)} quote={quote} />
            <PreviewQuotePdfModal isOpen={previewOpen}  onClose={() => setPreviewOpen(false)}   quote={quote} />
            <DeleteQuoteModal    isOpen={deleteOpen}    onClose={() => setDeleteOpen(false)}    quote={quote} />
            <SendQuoteModal      isOpen={sendOpen}      onClose={() => setSendOpen(false)}      quote={quote} />

            {/* Upgrade flow for admins on Solo who tap a Pro-locked button. */}
            {isAdmin && usage && (
                <ChangePlanModal
                    isOpen={planModalOpen}
                    toggle={() => setPlanModalOpen(false)}
                    currentTier={usage.tier}
                    pendingTier={usage.pendingTier}
                />
            )}
        </div>
    );
}
